import { MenuItem, Text } from "@chakra-ui/react";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { maybePluralize } from "src/services/format";
import type { FormSchoolStatus, SchoolFormId } from "../types";
import { canRevokeOffers } from "../validations/revokeOffers";
import { useBulkActionTargets } from "./bulkActionTargetsContext";
import { trackFormAction } from "./trackFormAction";
import { useEnqueueBulkOffer } from "./useEnqueueBulkOffer";

const confirmationBodyText = (count: number) =>
  maybePluralize(
    count,
    "Revoking this offer will prevent or undo the family's acceptance.",
    "Revoking these offers will prevent or undo the families' acceptance."
  );

const confirmButtonLabel = (count: number) =>
  maybePluralize(count, "Yes, revoke offer", "Yes, revoke offers");

interface Props {
  selectedFormSchoolStatuses: FormSchoolStatus[];
  selectedFormSchools: SchoolFormId[];
}

export const BulkRevokeOffer = ({
  selectedFormSchoolStatuses,
  selectedFormSchools,
}: Props) => {
  const bulk = useBulkActionTargets();
  const enqueueBulkOffer = useEnqueueBulkOffer();

  const { confirm, confirmationDialog } = useConfirmationDialog({
    header: null,
    body: null,
    cancelButton: {
      label: "Cancel",
    },
    translate: true,
  });

  const handleOnClick = async () => {
    const count = bulk.selectionSize;
    trackFormAction({
      verb: "Clicked",
      actionType: "REVOKE_OFFER",
      bulk: true,
      count,
      async: true,
    });

    if (
      !(await confirm({
        header: (
          <Text>{`Revoke ${count} ${maybePluralize(count, "offer")}?`}</Text>
        ),
        body: <Text>{confirmationBodyText(count)}</Text>,
        confirmButton: {
          label: confirmButtonLabel(count),
          colorScheme: "blue",
        },
      }))
    ) {
      trackFormAction({
        verb: "Canceled",
        actionType: "REVOKE_OFFER",
        bulk: true,
        count,
        async: true,
      });
      return;
    }

    trackFormAction({
      verb: "Confirmed",
      actionType: "REVOKE_OFFER",
      bulk: true,
      count,
      async: true,
    });
    await enqueueBulkOffer({ actionType: "REVOKE_OFFER" });
  };

  return (
    <>
      <MenuItem
        textColor="gray.700"
        fontWeight="400"
        fontSize="sm"
        onClick={handleOnClick}
        isDisabled={
          !canRevokeOffers(selectedFormSchoolStatuses, selectedFormSchools)
        }
      >
        Revoke offers
      </MenuItem>
      {confirmationDialog}
    </>
  );
};
